import { useHookstate } from "@hookstate/core";
import { ablyStore } from "../../../hookstate/ablyStore";
import {
  ChatClientProvider,
  ChatRoomProvider,
  RoomOptionsDefaults,
} from "@ably/chat";
import { AblyProvider, ChannelProvider } from "ably/react";
import * as Ably from "ably";
import { ChatClient } from "@ably/chat";
import { useLayoutEffect } from "react";
import { apiClient } from "../../../services/axiosInstance";
import { ServerResponse } from "../../../types";
import { useAccountData } from "../../../hooks/useAccountData";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

type AblyTokenData = {
  token: string;
  expires: number;
  issued: number;
  capability: string;
  clientId: string;
};

const fetchNewToken = () => {
  return apiClient.get<ServerResponse<AblyTokenData>>(`/v1/account/ably-token`);
};

export function AblyWrapper({
  children,
  channelId,
  featureFlag = "chat",
}: {
  children: React.ReactNode;
  channelId: string;
  featureFlag?: string;
}) {
  const accountData = useAccountData();
  const { chatClient, realtimeClient } = useHookstate(ablyStore).get({
    noproxy: true,
  }) as { chatClient?: ChatClient; realtimeClient?: Ably.RealtimeClient };

  const authCallback = async (
    _: Ably.TokenDetails,
    callback: (
      error: Ably.ErrorInfo | null,
      token: AblyTokenData | null,
    ) => void,
  ) => {
    let tokenResults: AblyTokenData;
    try {
      const newTokenResponse = await fetchNewToken();
      tokenResults = newTokenResponse.data.payload.results;
    } catch (error: Ably.ErrorInfo | any) {
      callback(error, null);
      return;
    }
    callback(null, tokenResults);
  };

  useFeatureFlag(featureFlag);

  useLayoutEffect(() => {
    const { realtimeClient: oldRealtimeClient } = ablyStore.get();

    if (oldRealtimeClient) {
      ablyStore.set({ realtimeClient: undefined, chatClient: undefined });
      oldRealtimeClient.close();
    }

    if (!accountData) {
      return;
    }

    const realtimeClient = new Ably.Realtime({
      authCallback,
      closeOnUnload: true,
    });
    const chatClient = new ChatClient(realtimeClient);

    ablyStore.merge({
      realtimeClient,
      chatClient,
    });
  }, [accountData]);

  if (!realtimeClient || !chatClient) {
    return null;
  }

  return (
    <AblyProvider client={realtimeClient}>
      <ChannelProvider channelName="gc:broadcast">
        <ChatClientProvider client={chatClient} attach={false}>
          <ChatRoomProvider id={channelId} options={RoomOptionsDefaults}>
            {children}
          </ChatRoomProvider>
        </ChatClientProvider>
      </ChannelProvider>
    </AblyProvider>
  );
}
